import {
  Button,
  FileArea,
  FileLink,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import ProductService from "@/services/productService";

import DocumentLinkCard from "@/components/products/edit/documentLinkCard";
import SerialNumberInput from "@/components/products/serialNumberInput";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

// EPS-4850 B-5 fix: module-level `import { t } from "i18next"` runtime dil
// değişiminde re-render tetiklemez. DocumentsContainer aşağıda kendi
// useTranslation() hook'unu kullanıyor.
import { TrashIcon } from "lucide-react";
import { z } from "zod";

import CategoryCombobox, {
  isNewValue,
  newValueName,
} from "@/components/shared/CategoryCombobox";
import CheckRole from "@/components/shared/checkRole";
import ConfirmationDialog from "@/components/shared/confirmDialog";

const LogoPreview = ({ imageUrl, onDelete, label }) => {
  const { t } = useTranslation();

  const openImageInNewTab = () => {
    if (imageUrl && !imageUrl.includes("placehold.co")) {
      window.open(imageUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-gray-900">{label}</span>
      <div
        className="relative flex h-36 w-36 cursor-pointer items-center justify-center rounded-md bg-gray-50 p-2"
        onClick={openImageInNewTab}
        role="button"
        tabIndex={0}
        aria-label={t("productsPage.viewProductImage")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            openImageInNewTab();
            e.preventDefault();
          }
        }}
      >
        <img
          src={imageUrl}
          alt={t("productsPage.productImage")}
          className="max-h-full max-w-full object-contain"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://placehold.co/150x150?text=No+Image";
          }}
        />
        <button
          onClick={(e) => {
            onDelete();
            e.stopPropagation();
          }}
          type="button"
          className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600 shadow-sm hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          title={t("common.delete")}
          aria-label={t("common.delete")}
        >
          <span className="sr-only">{t("common.delete")}</span>
          <TrashIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const ProductEditPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  // EPS-4850 B-2: belge fetch ayrı state — paylaşımlı loading flag form skeleton'unu
  // documents-from-BE'ye bağlıyordu; documents product DTO'sundan önce dönerse
  // form aktif olur ama defaultValues null kalır, user empty submit yapardı.
  const [docsLoading, setDocsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  // EPS-4850: kategori resmi/serino bilgisi yönetimi için product DTO state'i
  // (form sadece field değerlerini tutuyor; kategori ID'leri ham product DTO'sunda)
  const [productData, setProductData] = useState(null);
  // EPS-4867 — 4-level UI yapısı:
  //   Ana Kategori   = mainCategoryId      (1. seviye, BE: MainCategory)
  //   Kategori       = categoryId          (2. seviye, BE: Category)
  //   Alt Kategori   = subCategoryId       (3. seviye, BE: SubCategory)
  //   Marka          = brandId             (4. seviye, BE: SubCategoryBrand) — image YOK
  //   Ürün           = merchantProductId
  const [mainCategoryImageUrl, setMainCategoryImageUrl] = useState("");
  const [mainImageUploading, setMainImageUploading] = useState(false);
  const mainCategoryImageInputRef = useRef(null);

  const [categoryImageUrl, setCategoryImageUrl] = useState("");
  const [categorySerialNoInfo, setCategorySerialNoInfo] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [serialNoInfoSaving, setSerialNoInfoSaving] = useState(false);
  const categoryImageInputRef = useRef(null);

  // EPS-4867 — Alt Kategori (subCategory) görseli (3. seviye)
  const [subCategoryImageUrl, setSubCategoryImageUrl] = useState("");
  const [subImageUploading, setSubImageUploading] = useState(false);
  const subCategoryImageInputRef = useRef(null);

  // EPS-4867 — Marka (subCategoryBrand) görseli (4. seviye)
  const [brandImageUrl, setBrandImageUrl] = useState("");
  const [brandImageUploading, setBrandImageUploading] = useState(false);
  const brandImageInputRef = useRef(null);

  // 4-level cascade listeleri
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  const formSchema = z.object({
    // 4-step cascade — id'ler form state'inde, name'ler submit sırasında lookup'tan
    mainCategoryId: z
      .union([z.string(), z.number()])
      .refine((v) => v !== "" && v != null, {
        message: t("common.validateMsg.requiredField"),
      }),
    categoryId: z
      .union([z.string(), z.number()])
      .refine((v) => v !== "" && v != null, {
        message: t("common.validateMsg.requiredField"),
      }),
    subCategoryId: z
      .union([z.string(), z.number()])
      .refine((v) => v !== "" && v != null, {
        message: t("common.validateMsg.requiredField"),
      }),
    brandId: z
      .union([z.string(), z.number()])
      .refine((v) => v !== "" && v != null, {
        message: t("common.validateMsg.requiredField"),
      }),
    name: z.string().min(1, { message: t("common.validateMsg.requiredField") }),
    model: z.string().optional().nullable(),
    warrantyDuration: z
      .number({ required_error: t("common.validateMsg.requiredField") })
      .min(1, { message: t("common.validateMsg.requiredField") }),
    price: z
      .number({ required_error: t("common.validateMsg.requiredField") })
      .min(0, { message: t("common.validateMsg.requiredField") }),
    priceUnit: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    additionalService: z.boolean(),
    setupRequired: z.boolean(),
    serialNumber: z.array(z.string()).optional().default([]),
    description: z.string().optional(),
    merchantProductVideoList: z
      .array(
        z.object({
          videoTitle: z.string().optional().default(""),
          videoURL: z.string().optional().default(""),
        }),
      )
      .optional()
      .default([]),
    // form.watch("imageUrl") + form.setValue("imageUrl", ...) schema'ya bağlı;
    // BE API getProduct response'unda imageUrl alanı var, edit sırasında
    // null'a indirilebiliyor.
    imageUrl: z.string().nullable().optional(),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: async () => {
      // EPS-4850 B-2 fix: ürün DTO yükleme tamamlandığında loading false.
      // Eskiden fetchDocuments setLoading(false) yapıyordu (race) — şimdi form
      // mount loading'i KENDİ async defaultValues'una bağlı.
      const data = await ProductService.getProduct(id);
      setProductData(data);

      // 1. Kategori (mainCategoryId) görselini çek
      if (data?.mainCategoryId) {
        try {
          const mains = await ProductService.getMainCategories();
          const list = mains?.data?.data || mains?.data || mains || [];
          setMainCategories(Array.isArray(list) ? list : []);
          const main = (Array.isArray(list) ? list : []).find(
            (m) => Number(m.id) === Number(data.mainCategoryId),
          );
          if (main) setMainCategoryImageUrl(main.imageUrl || "");
        } catch {
          // sessiz — yan özellik, ana akışı bozmasın
        }
      }

      // 2. Kategori (categoryId) görseli + seri no bilgisini çek
      if (data?.mainCategoryId && data?.categoryId) {
        try {
          const cats = await ProductService.getCategories(data.mainCategoryId);
          const list = cats?.data || cats || [];
          setCategories(Array.isArray(list) ? list : []);
          const cat = (Array.isArray(list) ? list : []).find(
            (c) => Number(c.id) === Number(data.categoryId),
          );
          if (cat) {
            setCategoryImageUrl(cat.imageUrl || "");
            setCategorySerialNoInfo(cat.serialNoInfo || "");
          }
        } catch {
          // sessiz
        }
      }

      // 3. Alt Kategori (subCategoryId) görselini çek
      if (data?.categoryId && data?.subCategoryId) {
        try {
          const subs = await ProductService.getSubCategories(data.categoryId);
          const list = subs?.data || subs || [];
          setSubCategories(Array.isArray(list) ? list : []);
          const sub = (Array.isArray(list) ? list : []).find(
            (s) => Number(s.id) === Number(data.subCategoryId),
          );
          if (sub) setSubCategoryImageUrl(sub.imageUrl || "");
        } catch {
          // sessiz
        }
      }

      // 4. Marka (brandId) listesini + görselini çek
      if (data?.subCategoryId && data?.subCategoryBrandId) {
        try {
          const br = await ProductService.getSubCategoryBrands(data.subCategoryId);
          const list = br?.data || br || [];
          setBrands(Array.isArray(list) ? list : []);
          const brand = (Array.isArray(list) ? list : []).find(
            (b) => Number(b.id) === Number(data.subCategoryBrandId),
          );
          if (brand) setBrandImageUrl(brand.imageUrl || "");
        } catch {
          // sessiz
        }
      }

      // form mount loading'ini burada çöz; document fetch ayrı (docsLoading).
      setLoading(false);

      return {
        ...data,
        // Form state'inde id'ler — Select component string bekler.
        mainCategoryId: data?.mainCategoryId ? String(data.mainCategoryId) : "",
        categoryId: data?.categoryId ? String(data.categoryId) : "",
        subCategoryId: data?.subCategoryId ? String(data.subCategoryId) : "",
        brandId: data?.subCategoryBrandId ? String(data.subCategoryBrandId) : "",
        serialNumber: Array.isArray(data.serialNumber) ? data.serialNumber : [],
      };
    },
  });

  // Kullanıcı kategori cascade'ini değiştirirse listeleri taze yükle
  const watchedMainCategoryId = form.watch("mainCategoryId");
  const watchedCategoryId = form.watch("categoryId");
  const watchedSubCategoryId = form.watch("subCategoryId");
  const watchedBrandId = form.watch("brandId");

  useEffect(() => {
    if (mainCategories.length > 0) return; // defaultValues zaten yükledi
    setLoadingMain(true);
    ProductService.getMainCategories()
      .then((res) => {
        const list = res?.data?.data || res?.data || res || [];
        setMainCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => setMainCategories([]))
      .finally(() => setLoadingMain(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EPS-4850 B-7 fix: cancellation token — hızlı main category değişiminde
  // stale response son response'u override etmesin. Create page'de bu pattern
  // zaten doğru kullanılmış; edit page'i de hizala.
  useEffect(() => {
    if (!watchedMainCategoryId) {
      setCategories([]);
      return;
    }
    // Yeni (henüz oluşturulmamış) ana kategorinin alt kaydı yoktur — fetch etme.
    if (isNewValue(watchedMainCategoryId)) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    setLoadingCategories(true);
    ProductService.getCategories(watchedMainCategoryId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCategories(false);
      });
    return () => {
      cancelled = true;
    };
  }, [watchedMainCategoryId]);

  // Kategori değişince mainCategoryImageUrl tazele
  useEffect(() => {
    if (!watchedMainCategoryId || mainCategories.length === 0) return;
    const main = mainCategories.find(
      (m) => Number(m.id) === Number(watchedMainCategoryId),
    );
    setMainCategoryImageUrl(main?.imageUrl || "");
  }, [watchedMainCategoryId, mainCategories]);

  // Kategori değişince categoryImageUrl + serialNoInfo tazele + sub-categories çek
  useEffect(() => {
    if (!watchedCategoryId || categories.length === 0) return;
    const cat = categories.find(
      (c) => Number(c.id) === Number(watchedCategoryId),
    );
    setCategoryImageUrl(cat?.imageUrl || "");
    setCategorySerialNoInfo(cat?.serialNoInfo || "");
  }, [watchedCategoryId, categories]);

  // EPS-4867 — categoryId değişince sub-categories listesini çek
  useEffect(() => {
    if (!watchedCategoryId) {
      setSubCategories([]);
      return;
    }
    // Yeni kategorinin alt kategorisi yoktur — fetch etme.
    if (isNewValue(watchedCategoryId)) {
      setSubCategories([]);
      return;
    }
    let cancelled = false;
    setLoadingSubs(true);
    ProductService.getSubCategories(watchedCategoryId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setSubCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSubCategories([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [watchedCategoryId]);

  // EPS-4867 — subCategoryId değişince image + brands listesini çek
  useEffect(() => {
    if (!watchedSubCategoryId || subCategories.length === 0) return;
    const sub = subCategories.find(
      (s) => Number(s.id) === Number(watchedSubCategoryId),
    );
    setSubCategoryImageUrl(sub?.imageUrl || "");
  }, [watchedSubCategoryId, subCategories]);

  // EPS-4867 — brand değişince image güncelle
  useEffect(() => {
    if (!watchedBrandId || brands.length === 0) {
      setBrandImageUrl("");
      return;
    }
    const brand = brands.find((b) => Number(b.id) === Number(watchedBrandId));
    setBrandImageUrl(brand?.imageUrl || "");
  }, [watchedBrandId, brands]);

  useEffect(() => {
    if (!watchedSubCategoryId) {
      setBrands([]);
      return;
    }
    // Yeni alt kategorinin markası yoktur — fetch etme.
    if (isNewValue(watchedSubCategoryId)) {
      setBrands([]);
      return;
    }
    let cancelled = false;
    setLoadingBrands(true);
    ProductService.getSubCategoryBrands(watchedSubCategoryId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setBrands(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setBrands([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingBrands(false);
      });
    return () => {
      cancelled = true;
    };
  }, [watchedSubCategoryId]);

  // 1. Kategori (mainCategoryId) görseli — UPLOAD
  const handleMainCategoryImageUpload = async (event) => {
    const file = event.target.files?.[0];
    const mainId = form.getValues("mainCategoryId");
    if (!file || !mainId) return;
    setMainImageUploading(true);
    try {
      const uploadResult = await ProductService.uploadFile(file);
      const url = uploadResult?.url || uploadResult?.data?.url;
      if (!url) throw new Error("Upload response missing url");
      const updated = await ProductService.updateMainCategoryImage(mainId, url);
      setMainCategoryImageUrl(updated?.imageUrl || url);
      toast({
        title: t(
          "productsPage.mainCategoryImageUpdateSuccess",
          "Kategori görseli güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.mainCategoryImageUpdateError", "Görsel yüklenemedi"),
        variant: "error",
      });
    } finally {
      setMainImageUploading(false);
      event.target.value = "";
    }
  };

  const handleMainCategoryImageDelete = async () => {
    const mainId = form.getValues("mainCategoryId");
    if (!mainId) return;
    setMainImageUploading(true);
    try {
      await ProductService.deleteMainCategoryImage(mainId);
      setMainCategoryImageUrl("");
      toast({
        title: t(
          "productsPage.mainCategoryImageDeleteSuccess",
          "Kategori görseli silindi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.mainCategoryImageDeleteError",
            "Kategori görseli silinemedi",
          ),
        variant: "error",
      });
    } finally {
      setMainImageUploading(false);
    }
  };

  // 2. Alt Kategori (categoryId) görseli — UPLOAD + DELETE
  const handleCategoryImageUpload = async (event) => {
    const file = event.target.files?.[0];
    const catId = form.getValues("categoryId");
    if (!file || !catId) return;
    setImageUploading(true);
    try {
      const uploadResult = await ProductService.uploadFile(file);
      const url = uploadResult?.url || uploadResult?.data?.url;
      if (!url) throw new Error("Upload response missing url");
      const updated = await ProductService.updateCategoryImage(catId, url);
      setCategoryImageUrl(updated?.imageUrl || url);
      toast({
        title: t(
          "productsPage.categoryImageUpdateSuccess",
          "Alt kategori görseli güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.categoryImageUpdateError",
            "Alt kategori görseli yüklenemedi",
          ),
        variant: "error",
      });
    } finally {
      setImageUploading(false);
      event.target.value = "";
    }
  };

  const handleCategoryImageDelete = async () => {
    const catId = form.getValues("categoryId");
    if (!catId) return;
    setImageUploading(true);
    try {
      await ProductService.deleteCategoryImage(catId);
      setCategoryImageUrl("");
      toast({
        title: t(
          "productsPage.categoryImageDeleteSuccess",
          "Kategori görseli silindi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.categoryImageDeleteError",
            "Kategori görseli silinemedi",
          ),
        variant: "error",
      });
    } finally {
      setImageUploading(false);
    }
  };

  // EPS-4867 — Alt Kategori (subCategoryId) görseli — UPLOAD + DELETE
  const handleSubCategoryImageUpload = async (event) => {
    const file = event.target.files?.[0];
    const subId = form.getValues("subCategoryId");
    if (!file || !subId) return;
    setSubImageUploading(true);
    try {
      const uploadResult = await ProductService.uploadFile(file);
      const url = uploadResult?.url || uploadResult?.data?.url;
      if (!url) throw new Error("Upload response missing url");
      const updated = await ProductService.updateSubCategoryImage(subId, url);
      setSubCategoryImageUrl(updated?.imageUrl || url);
      toast({
        title: t(
          "productsPage.subCategoryImageUpdateSuccess",
          "Alt kategori görseli güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.subCategoryImageUpdateError",
            "Alt kategori görseli yüklenemedi",
          ),
        variant: "error",
      });
    } finally {
      setSubImageUploading(false);
      event.target.value = "";
    }
  };

  const handleSubCategoryImageDelete = async () => {
    const subId = form.getValues("subCategoryId");
    if (!subId) return;
    setSubImageUploading(true);
    try {
      await ProductService.deleteSubCategoryImage(subId);
      setSubCategoryImageUrl("");
      toast({
        title: t(
          "productsPage.subCategoryImageDeleteSuccess",
          "Alt kategori görseli silindi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.subCategoryImageDeleteError",
            "Alt kategori görseli silinemedi",
          ),
        variant: "error",
      });
    } finally {
      setSubImageUploading(false);
    }
  };

  // EPS-4867 — Marka (subCategoryBrand) görseli — UPLOAD + DELETE
  const handleBrandImageUpload = async (event) => {
    const file = event.target.files?.[0];
    const brandId = form.getValues("brandId");
    if (!file || !brandId) return;
    setBrandImageUploading(true);
    try {
      const uploadResult = await ProductService.uploadFile(file);
      const url = uploadResult?.url || uploadResult?.data?.url;
      if (!url) throw new Error("Upload response missing url");
      const updated = await ProductService.updateSubCategoryBrandImage(
        brandId,
        url,
      );
      setBrandImageUrl(updated?.imageUrl || url);
      toast({
        title: "Marka görseli güncellendi",
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          "Marka görseli yüklenemedi",
        variant: "error",
      });
    } finally {
      setBrandImageUploading(false);
      event.target.value = "";
    }
  };

  const handleBrandImageDelete = async () => {
    const brandId = form.getValues("brandId");
    if (!brandId) return;
    setBrandImageUploading(true);
    try {
      await ProductService.deleteSubCategoryBrandImage(brandId);
      setBrandImageUrl("");
      toast({
        title: "Marka görseli silindi",
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          "Marka görseli silinemedi",
        variant: "error",
      });
    } finally {
      setBrandImageUploading(false);
    }
  };

  // Alt Kategori (categoryId) seri no bilgisi kaydet
  const handleCategorySerialNoInfoSave = async () => {
    const catId = form.getValues("categoryId");
    if (!catId) return;
    setSerialNoInfoSaving(true);
    try {
      const updated = await ProductService.updateCategorySerialNoInfo(
        catId,
        categorySerialNoInfo,
      );
      setCategorySerialNoInfo(updated?.serialNoInfo ?? categorySerialNoInfo);
      toast({
        title: t(
          "productsPage.categorySerialNoInfoUpdateSuccess",
          "Seri no bilgisi güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.categorySerialNoInfoUpdateError",
            "Seri no bilgisi kaydedilemedi",
          ),
        variant: "error",
      });
    } finally {
      setSerialNoInfoSaving(false);
    }
  };

  const onSubmit = (data) => {
    const serialNumberField = form.getFieldState("serialNumber");
    // EPS-4867 — 4-step cascade'i name field'larına çevir (BE name bekliyor).
    // EPS-4899 — kullanıcı yeni ad yazdıysa (new::<ad>) doğrudan o adı gönder;
    // var olan seçildiyse listeden çöz, bulunamazsa eski DTO adına düş.
    const nameOf = (val, list, fallback) =>
      isNewValue(val)
        ? newValueName(val)
        : list.find((x) => String(x.id) === String(val))?.name ||
          fallback ||
          "";
    const dataToSend = {
      id,
      ...data,
      mainCategoryName: nameOf(
        data.mainCategoryId,
        mainCategories,
        productData?.mainCategoryName,
      ),
      categoryName: nameOf(
        data.categoryId,
        categories,
        productData?.categoryName,
      ),
      subCategoryName: nameOf(
        data.subCategoryId,
        subCategories,
        productData?.subCategoryName,
      ),
      brandName: nameOf(data.brandId, brands, productData?.brandName),
      serialNumber: serialNumberField.isDirty
        ? data.serialNumber?.length === 1 && data.serialNumber[0] === ""
          ? []
          : data.serialNumber || null
        : undefined,
    };
    // Form state'inde id'leri vardı; payload'a name'leri zaten ekledik.
    delete dataToSend.mainCategoryId;
    delete dataToSend.categoryId;
    delete dataToSend.subCategoryId;
    delete dataToSend.brandId;

    setLoading(true);

    ProductService.updateProduct(dataToSend)
      .then(() => {
        toast({
          title: t("productsPage.updateSuccess"),
          variant: "success",
        });
        navigate("/products/products");
      })
      .catch((err) => {
        toast({
          variant: "error",
          description: err.response?.data?.errors?.[0]?.message,
        });
      })
      .finally(() => setLoading(false));
  };

  // EPS-4850 B-2 fix: form'un global loading flag'ini documents'a bağlamayalım.
  // .catch eksikliği de loading'i sonsuza kilitleyebiliyordu — finally ile garantile.
  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      const data = await ProductService.getDocuments(id);
      // documents Zod schema'da yer almıyor; sadece local state'te tut.
      setDocuments(data);
    } catch (err) {
      toast({
        variant: "error",
        title: t("common.error", "Hata"),
        description:
          err.response?.data?.errors?.[0]?.message ||
          t("productsPage.fetchDocumentsError", "Belgeler yüklenemedi"),
      });
    } finally {
      setDocsLoading(false);
    }
  };
  useEffect(() => {
    fetchDocuments();
  }, []);

  // EPS-4850 B-3 fix: optimistic null-set'i kaldır. API success'inde form değerini
  // güncelle, fail'de mevcut imageUrl korunur ve user duplicate upload zorlanmaz.
  const onDeleteProductImage = () => {
    const previousImageUrl = form.getValues("imageUrl");
    ProductService.deleteProductImage(id)
      .then(() => {
        form.setValue("imageUrl", null);
      })
      .catch((err) => {
        // rollback: hiç yapılmayan iş yok — imageUrl zaten dokunulmadı.
        toast({
          variant: "error",
          description:
            err.response?.data?.errors?.[0]?.message ||
            t("productsPage.deleteImageError", "Görsel silinemedi"),
        });
        // belirsizlik için ek garanti: state'i eski değere zorla.
        form.setValue("imageUrl", previousImageUrl);
      });
  };

  const onImageUpload = (id, file) => {
    const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40 MB
    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: "error",
        title: t("productsPage.fileSizeError", "Dosya boyutu çok büyük"),
        description: t("productsPage.fileSizeErrorDesc", "Dosya boyutu maksimum 40 MB olmalıdır."),
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    ProductService.postProductImage(id, formData)
      .then((res) => {
        form.setValue("imageUrl", res.data.imageUrl);
        toast({
          title: t("productsPage.imageUploadSuccess"),
          variant: "success",
        });
      })
      .catch((err) => {
        toast({
          variant: "error",
          title: t("productsPage.imageUploadError", "Görsel yüklenemedi"),
          description: err.response?.data?.errors?.[0]?.message || t("productsPage.imageUploadErrorDesc", "Görsel yüklenirken bir hata oluştu."),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const videos = form.watch("merchantProductVideoList");

  const handleAddVideo = () => {
    const updatedVideos = [...videos, { videoTitle: "", videoURL: "" }];
    form.setValue("merchantProductVideoList", updatedVideos);
    form.trigger("merchantProductVideoList");
  };

  const handleDeleteVideo = (index) => {
    const updatedVideos = videos.filter((_, i) => i !== index);
    form.setValue("merchantProductVideoList", updatedVideos);
    form.trigger("merchantProductVideoList");
  };

  const handleVideoChange = (index, field, value) => {
    const updatedVideos = [...videos];
    updatedVideos[index] = {
      ...updatedVideos[index],
      [field]: value,
    };
    form.setValue("merchantProductVideoList", updatedVideos);
  };
  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC] pb-28">
      <Breadcrumb>
        <BreadcrumbItem link="/products/products">
          {t("productsPage.products")}
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("productsPage.editProduct")}</BreadcrumbItem>
      </Breadcrumb>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="container mx-auto my-20 flex flex-col items-center justify-center gap-6">
            {/* 2-step cascade — Kategori (Main) + Alt Kategori (Category) */}
            <div className="formBox flex flex-col gap-4">
              <h2 className="text-lg font-medium">
                {t("productsPage.productCategory", "Ürün Kategorisi")}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="mainCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.mainCategory", "Ana Kategori")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={mainCategories}
                          value={field.value || ""}
                          onChange={(v) => {
                            // Kullanıcı manuel değiştirirse tüm child cascade'i sıfırla
                            if (field.value && field.value !== v) {
                              form.setValue("categoryId", "");
                              form.setValue("subCategoryId", "");
                              form.setValue("brandId", "");
                            }
                            field.onChange(v);
                          }}
                          disabled={loadingMain}
                          loading={loadingMain}
                          placeholder={t(
                            "productsPage.selectMainCategory",
                            "Ana kategori seçin",
                          )}
                          ariaLabel={t(
                            "productsPage.mainCategory",
                            "Ana Kategori",
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.category", "Kategori")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={categories}
                          value={field.value || ""}
                          onChange={(v) => {
                            if (field.value && field.value !== v) {
                              form.setValue("subCategoryId", "");
                              form.setValue("brandId", "");
                            }
                            field.onChange(v);
                          }}
                          disabled={!watchedMainCategoryId || loadingCategories}
                          loading={loadingCategories}
                          placeholder={
                            !watchedMainCategoryId
                              ? t(
                                  "productsPage.selectMainCategoryFirst",
                                  "Önce ana kategori seçin",
                                )
                              : t("productsPage.selectCategory", "Kategori seçin")
                          }
                          ariaLabel={t("productsPage.category", "Kategori")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* EPS-4867 — 3. seviye: Alt Kategori (subCategoryId) */}
                <FormField
                  control={form.control}
                  name="subCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.subCategory", "Alt Kategori")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={subCategories}
                          value={field.value || ""}
                          onChange={(v) => {
                            if (field.value && field.value !== v) {
                              form.setValue("brandId", "");
                            }
                            field.onChange(v);
                          }}
                          disabled={!watchedCategoryId || loadingSubs}
                          loading={loadingSubs}
                          placeholder={
                            !watchedCategoryId
                              ? t(
                                  "productsPage.selectCategoryFirst",
                                  "Önce kategori seçin",
                                )
                              : t(
                                  "productsPage.selectSubCategory",
                                  "Alt kategori seçin",
                                )
                          }
                          ariaLabel={t(
                            "productsPage.subCategory",
                            "Alt Kategori",
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* EPS-4867 — 4. seviye: Marka (brandId) — image yok */}
                <FormField
                  control={form.control}
                  name="brandId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.brand", "Marka")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={brands}
                          value={field.value || ""}
                          onChange={field.onChange}
                          disabled={!watchedSubCategoryId || loadingBrands}
                          loading={loadingBrands}
                          placeholder={
                            !watchedSubCategoryId
                              ? t(
                                  "productsPage.selectSubCategoryFirst",
                                  "Önce alt kategori seçin",
                                )
                              : t("productsPage.selectBrand", "Marka seçin")
                          }
                          ariaLabel={t("productsPage.brand", "Marka")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* 1. Kategori (mainCategoryId) — görsel */}
            {watchedMainCategoryId && !isNewValue(watchedMainCategoryId) && (
              <div className="formBox flex flex-col gap-4">
                <h2 className="text-lg font-medium">
                  {t("productsPage.mainCategoryImageTitle", "Ana Kategori Görseli")}
                </h2>
                <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.mainCategoryImageSharedNote",
                    "Bu görsel seçilen ana kategori için ortaktır; aynı ana kategoriyi kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <div className="flex items-start gap-3">
                  {mainCategoryImageUrl ? (
                    <img
                      src={mainCategoryImageUrl}
                      alt={t("productsPage.mainCategory", "Kategori")}
                      className="h-20 w-20 rounded-md border object-cover"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed bg-gray-50 text-xs text-gray-400">
                      {t("productsPage.noImage", "Görsel yok")}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => mainCategoryImageInputRef.current?.click()}
                    disabled={mainImageUploading}
                    aria-label={
                      mainCategoryImageUrl
                        ? t(
                            "productsPage.changeMainCategoryImage",
                            "Kategori görselini değiştir",
                          )
                        : t(
                            "productsPage.uploadMainCategoryImage",
                            "Kategori görseli yükle",
                          )
                    }
                    className="inline-flex h-11 cursor-pointer items-center rounded-md border bg-white px-3 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {mainImageUploading
                      ? t("common.loading", "Yükleniyor...")
                      : mainCategoryImageUrl
                        ? t("productsPage.changeImage", "Görseli değiştir")
                        : t("productsPage.uploadImage", "Görsel yükle")}
                  </button>
                  {mainCategoryImageUrl && !mainImageUploading && (
                    <ConfirmationDialog
                      title={t(
                        "productsPage.deleteImageConfirmTitle",
                        "Görseli silmek istiyor musunuz?",
                      )}
                      description={t(
                        "productsPage.deleteImageConfirmDescription",
                        "Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?",
                      )}
                      cancelText={t("common.no", "Hayır")}
                      confirmText={t("common.yes", "Evet")}
                      onConfirm={handleMainCategoryImageDelete}
                    >
                      <button
                        type="button"
                        aria-label={t(
                          "productsPage.deleteMainCategoryImage",
                          "Kategori görselini sil",
                        )}
                        className="inline-flex h-11 cursor-pointer items-center rounded-md border border-red-300 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        {t("common.delete", "Sil")}
                      </button>
                    </ConfirmationDialog>
                  )}
                  <input
                    ref={mainCategoryImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleMainCategoryImageUpload}
                    disabled={mainImageUploading}
                  />
                </div>
              </div>
            )}

            {/* 2. Kategori (categoryId) — görsel + seri no bilgisi */}
            {watchedCategoryId && !isNewValue(watchedCategoryId) && (
              <div className="formBox flex flex-col gap-4">
                <h2 className="text-lg font-medium">
                  {t(
                    "productsPage.categoryImageAndSerialNoTitle",
                    "Kategori Görseli ve Seri No Bilgisi",
                  )}
                </h2>
                <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.categoryImageAndSerialNoSharedNote",
                    "Bu görsel ve seri no bilgisi seçilen kategori için ortaktır; aynı kategoriyi kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Kategori görseli */}
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {t("productsPage.categoryImage", "Kategori Görseli")}
                    </span>
                    <div className="flex flex-wrap items-start gap-3">
                      {categoryImageUrl ? (
                        <img
                          src={categoryImageUrl}
                          alt={t(
                            "productsPage.subCategoryImage",
                            "Alt Kategori Görseli",
                          )}
                          className="h-20 w-20 rounded-md border object-cover"
                        />
                      ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed bg-gray-50 text-xs text-gray-400">
                          {t("productsPage.noImage", "Görsel yok")}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => categoryImageInputRef.current?.click()}
                        disabled={imageUploading}
                        aria-label={
                          categoryImageUrl
                            ? t(
                                "productsPage.changeSubCategoryImage",
                                "Alt kategori görselini değiştir",
                              )
                            : t(
                                "productsPage.uploadSubCategoryImage",
                                "Alt kategori görseli yükle",
                              )
                        }
                        className="inline-flex h-11 cursor-pointer items-center rounded-md border bg-white px-3 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {imageUploading
                          ? t("common.loading", "Yükleniyor...")
                          : categoryImageUrl
                            ? t("productsPage.changeImage", "Görseli değiştir")
                            : t("productsPage.uploadImage", "Görsel yükle")}
                      </button>
                      {categoryImageUrl && !imageUploading && (
                        <ConfirmationDialog
                          title={t(
                            "productsPage.deleteImageConfirmTitle",
                            "Görseli silmek istiyor musunuz?",
                          )}
                          description={t(
                            "productsPage.deleteImageConfirmDescription",
                            "Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?",
                          )}
                          cancelText={t("common.no", "Hayır")}
                          confirmText={t("common.yes", "Evet")}
                          onConfirm={handleCategoryImageDelete}
                        >
                          <button
                            type="button"
                            aria-label={t(
                              "productsPage.deleteSubCategoryImage",
                              "Alt kategori görselini sil",
                            )}
                            className="inline-flex h-11 cursor-pointer items-center rounded-md border border-red-300 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                          >
                            {t("common.delete", "Sil")}
                          </button>
                        </ConfirmationDialog>
                      )}
                      <input
                        ref={categoryImageInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleCategoryImageUpload}
                        disabled={imageUploading}
                      />
                    </div>
                  </div>

                  {/* Kategori seri no bilgisi */}
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="categorySerialNoInfo"
                      className="text-sm font-medium text-gray-900"
                    >
                      {t(
                        "productsPage.categorySerialNoInfo",
                        "Seri No Bilgisi",
                      )}
                    </label>
                    <Textarea
                      id="categorySerialNoInfo"
                      value={categorySerialNoInfo}
                      onChange={(e) => setCategorySerialNoInfo(e.target.value)}
                      placeholder={t(
                        "productsPage.categorySerialNoInfoPlaceholder",
                        "Örn: Cihazın arkasında IMEI olarak yazar",
                      )}
                      rows={3}
                    />
                    <Button
                      type="button"
                      onClick={handleCategorySerialNoInfoSave}
                      disabled={serialNoInfoSaving}
                      className="self-start"
                    >
                      {serialNoInfoSaving
                        ? t("common.saving", "Kaydediliyor...")
                        : t(
                            "productsPage.saveCategorySerialNoInfo",
                            "Seri No Bilgisini Kaydet",
                          )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* EPS-4867 — 3. Alt Kategori (subCategoryId) görseli */}
            {watchedSubCategoryId && !isNewValue(watchedSubCategoryId) && (
              <div className="formBox flex flex-col gap-4">
                <h2 className="text-lg font-medium">
                  {t("productsPage.subCategoryImageTitle", "Alt Kategori Görseli")}
                </h2>
                <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.subCategoryImageSharedNote",
                    "Bu görsel seçilen alt kategori için ortaktır; aynı alt kategoriyi kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <div className="flex items-start gap-3">
                  {subCategoryImageUrl ? (
                    <img
                      src={subCategoryImageUrl}
                      alt={t("productsPage.subCategory", "Alt Kategori")}
                      className="h-20 w-20 rounded-md border object-cover"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed bg-gray-50 text-xs text-gray-400">
                      {t("productsPage.noImage", "Görsel yok")}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => subCategoryImageInputRef.current?.click()}
                    disabled={subImageUploading}
                    aria-label={
                      subCategoryImageUrl
                        ? t(
                            "productsPage.changeSubCategoryImage",
                            "Alt kategori görselini değiştir",
                          )
                        : t(
                            "productsPage.uploadSubCategoryImage",
                            "Alt kategori görseli yükle",
                          )
                    }
                    className="inline-flex h-11 cursor-pointer items-center rounded-md border bg-white px-3 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {subImageUploading
                      ? t("common.loading", "Yükleniyor...")
                      : subCategoryImageUrl
                        ? t("productsPage.changeImage", "Görseli değiştir")
                        : t("productsPage.uploadImage", "Görsel yükle")}
                  </button>
                  {subCategoryImageUrl && !subImageUploading && (
                    <ConfirmationDialog
                      title={t(
                        "productsPage.deleteImageConfirmTitle",
                        "Görseli silmek istiyor musunuz?",
                      )}
                      description={t(
                        "productsPage.deleteImageConfirmDescription",
                        "Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?",
                      )}
                      cancelText={t("common.no", "Hayır")}
                      confirmText={t("common.yes", "Evet")}
                      onConfirm={handleSubCategoryImageDelete}
                    >
                      <button
                        type="button"
                        aria-label={t(
                          "productsPage.deleteSubCategoryImage",
                          "Alt kategori görselini sil",
                        )}
                        className="inline-flex h-11 cursor-pointer items-center rounded-md border border-red-300 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        {t("common.delete", "Sil")}
                      </button>
                    </ConfirmationDialog>
                  )}
                  <input
                    ref={subCategoryImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleSubCategoryImageUpload}
                    disabled={subImageUploading}
                  />
                </div>
              </div>
            )}

            {/* EPS-4867 — 4. Marka (brandId) görseli */}
            {watchedBrandId && !isNewValue(watchedBrandId) && (
              <div className="formBox flex flex-col gap-4">
                <h2 className="text-lg font-medium">
                  {t("productsPage.brandImageTitle", "Marka Görseli")}
                </h2>
                <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.brandImageSharedNote",
                    "Bu görsel seçilen marka için ortaktır; aynı markayı kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <div className="flex items-start gap-3">
                  {brandImageUrl ? (
                    <img
                      src={brandImageUrl}
                      alt={t("extendedWarranty.add.brandLabel", "Marka")}
                      className="h-20 w-20 rounded-md border object-cover"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed bg-gray-50 text-xs text-gray-400">
                      {t("productsPage.noImage", "Görsel yok")}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => brandImageInputRef.current?.click()}
                    disabled={brandImageUploading}
                    aria-label={
                      brandImageUrl
                        ? t(
                            "productsPage.changeBrandImage",
                            "Marka görselini değiştir",
                          )
                        : t(
                            "productsPage.uploadBrandImage",
                            "Marka görseli yükle",
                          )
                    }
                    className="inline-flex h-11 cursor-pointer items-center rounded-md border bg-white px-3 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {brandImageUploading
                      ? t("common.loading", "Yükleniyor...")
                      : brandImageUrl
                        ? t("productsPage.changeImage", "Görseli değiştir")
                        : t("productsPage.uploadImage", "Görsel yükle")}
                  </button>
                  {brandImageUrl && !brandImageUploading && (
                    <ConfirmationDialog
                      title={t(
                        "productsPage.deleteImageConfirmTitle",
                        "Görseli silmek istiyor musunuz?",
                      )}
                      description={t(
                        "productsPage.deleteImageConfirmDescription",
                        "Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?",
                      )}
                      cancelText={t("common.no", "Hayır")}
                      confirmText={t("common.yes", "Evet")}
                      onConfirm={handleBrandImageDelete}
                    >
                      <button
                        type="button"
                        aria-label={t(
                          "productsPage.deleteBrandImage",
                          "Marka görselini sil",
                        )}
                        className="inline-flex h-11 cursor-pointer items-center rounded-md border border-red-300 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        {t("common.delete", "Sil")}
                      </button>
                    </ConfirmationDialog>
                  )}
                  <input
                    ref={brandImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBrandImageUpload}
                    disabled={brandImageUploading}
                  />
                </div>
              </div>
            )}

            <div className="formBox flex flex-col gap-4">
              <h2 className="mb-5 text-lg font-medium">
                {t("productsPage.productInfo")}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("productsPage.productName")}</FormLabel>
                      <FormControl>
                        <Input maxLength={70} placeholder="" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="model"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.productModel", "Model")}
                      </FormLabel>
                      <FormControl>
                        <Input maxLength={70} placeholder="" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="warrantyDuration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("productsPage.warrantyDuration")}</FormLabel>
                      <FormControl>
                        <Input
                          min="1"
                          placeholder=""
                          type="number"
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        {t("productsPage.warrantyDurationDesc")}
                      </FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("productsPage.description")}</FormLabel>
                      <FormControl>
                        <Textarea maxLength={1000} placeholder="" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex gap-4">
                  <FormField
                    control={form.control}
                    name="priceUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("productsPage.priceUnit")} *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="TRY">TRY</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                            <SelectItem value="UZS">UZS</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("productsPage.price")} *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            value={field.value}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {loading ? (
                  <div className="flex animate-pulse items-center justify-center rounded-lg bg-[#F2F5F9] p-2">
                    Loading...
                  </div>
                ) : (
                  <>
                    {(() => {
                      // EPS-4867 — Fallback hierarchy:
                      // product imageUrl → subCategoryImageUrl → categoryImageUrl → mainCategoryImageUrl
                      const productImg = form.watch("imageUrl");
                      if (productImg) {
                        return (
                          <LogoPreview
                            imageUrl={productImg}
                            onDelete={onDeleteProductImage}
                            label={t("productsPage.productImage", "Ürün Görseli")}
                          />
                        );
                      }
                      const fallbackUrl =
                        subCategoryImageUrl ||
                        categoryImageUrl ||
                        mainCategoryImageUrl ||
                        "";
                      const fallbackLevel = subCategoryImageUrl
                        ? t("productsPage.subCategory", "Alt Kategori")
                        : categoryImageUrl
                          ? t("productsPage.category", "Kategori")
                          : mainCategoryImageUrl
                            ? t("productsPage.mainCategory", "Ana Kategori")
                            : "";
                      if (fallbackUrl) {
                        return (
                          <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {t("productsPage.productImage", "Ürün Görseli")}
                            </span>
                            <div className="flex flex-wrap items-start gap-3 rounded-md border bg-gray-50 p-3">
                              <img
                                src={fallbackUrl}
                                alt={t("productsPage.productImage", "Ürün Görseli")}
                                className="h-20 w-20 rounded-md border object-cover"
                              />
                              <div className="flex flex-col gap-2">
                                <p className="text-xs text-gray-500">
                                  {`${fallbackLevel} görseli kullanılıyor. Üzerine yüklemek için aşağıdan dosya seçin.`}
                                </p>
                                <FileArea
                                  title={t("productsPage.uploadImage", "Görsel yükle")}
                                  onFileUpload={(file) =>
                                    onImageUpload(id, file)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <FileArea
                          title={t("productsPage.productImage", "Ürün Görseli")}
                          onFileUpload={(file) => onImageUpload(id, file)}
                        />
                      );
                    })()}
                  </>
                )}
                <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                  <FormField
                    control={form.control}
                    name="setupRequired"
                    render={({ field }) => (
                      <FormItem className="flex flex-col py-4">
                        <FormLabel>
                          {t("productsPage.installationRequired")}
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <hr className="hidden h-12 w-px bg-[#E0E0E0] sm:block" />
                  <FormField
                    control={form.control}
                    name="additionalService"
                    render={({ field }) => (
                      <FormItem className="flex flex-col py-4">
                        <FormLabel>
                          {t("productsPage.additionalService")}
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="formBox flex flex-col gap-4">
              <h2 className="mb-5 text-lg font-medium">
                {t("productsPage.productVideos")}
              </h2>
              {videos?.map((video, index) => (
                <div
                  key={video.id ?? video.videoURL ?? `video-${index}`}
                  className="grid grid-cols-1 items-center gap-4 border-b pb-6 sm:grid-cols-2"
                >
                  <FormField
                    control={form.control}
                    name={`merchantProductVideoList[${index}].videoTitle`}
                    render={() => (
                      <FormItem>
                        <FormLabel>{t("productsPage.videoTitle")}</FormLabel>
                        <FormControl>
                          <Input
                            value={video.videoTitle || ""}
                            onChange={(e) =>
                              handleVideoChange(
                                index,
                                "videoTitle",
                                e.target.value,
                              )
                            }
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`merchantProductVideoList[${index}].videoURL`}
                    render={() => (
                      <FormItem>
                        <FormLabel>{t("productsPage.videoURL")}</FormLabel>
                        <FormControl>
                          <Input
                            value={video.videoURL || ""}
                            onChange={(e) =>
                              handleVideoChange(
                                index,
                                "videoURL",
                                e.target.value,
                              )
                            }
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <button
                    type="button"
                    className="col-span-2 text-right font-medium text-[#F04438]"
                    onClick={() => handleDeleteVideo(index)}
                  >
                    {t("productsPage.deleteVideo")}
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="self-start font-medium text-[#0040C9]"
                onClick={handleAddVideo}
              >
                {t("productsPage.addVideo")}
              </button>
            </div>
            {/* Serial Numbers Section */}
            <SerialNumberInput form={form} />
            <CheckRole section="PRODUCTS_LIST" action="EDIT">
              <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-white px-4 py-3 shadow-lg md:px-8">
                <div className="mx-auto flex w-full max-w-3xl justify-end">
                  <Button
                    size="lg"
                    disabled={loading}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {t("common.save")}
                  </Button>
                </div>
              </div>
            </CheckRole>
            <div className="formBox flex flex-col gap-4">
              <h2 className="mb-5 text-lg font-medium">
                {t("productsPage.documentInfo")}
              </h2>
              {docsLoading ? (
                <p>Loading...</p>
              ) : (
                <DocumentsContainer
                  reFetchDocuments={fetchDocuments}
                  documents={documents}
                />
              )}
            </div>
          </div>
        </form>
      </Form>
    </main>
  );
};

export default ProductEditPage;

const DocumentsContainer = ({ documents, reFetchDocuments }) => {
  // EPS-4850 B-5 fix: hook üzerinden t — runtime dil değişimi re-render.
  const { t } = useTranslation();
  return (
    <div className="flex flex-col justify-between gap-12 md:flex-row">
      <DocumentLinkCard
        reFetchDocuments={reFetchDocuments}
        title={t("productsPage.warrantyDocument")}
        type="WARRANTY_DOCUMENT"
        document={documents?.find((doc) => doc.type === "WARRANTY_DOCUMENT")}
      />
      <DocumentLinkCard
        reFetchDocuments={reFetchDocuments}
        title={t("productsPage.userManual")}
        type="USER_MANUAL_DOCUMENT"
        document={documents?.find((doc) => doc.type === "USER_MANUAL_DOCUMENT")}
      />
    </div>
  );
};
